/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useState } from 'react';
import * as Ron from 'blb-ui-types/ron';
import * as RonAPI from 'blb-api/ron';
import { getRONUser, patchUserStatus } from 'blb-api/ron';
import { Box, Heading } from 'grommet';
import { useThemeColor as c } from '@blend/alchemy';
import { FullScreenModal } from 'blb-components/GrommetComponents';
import { PageSectionHeader } from 'blb-components/AuthenticatedApp/RON/NotaryPage/PageSectionHeader';
import { ProfileInfo } from 'blb-components/AuthenticatedApp/ProfilePage/ProfileInfo';
import { CredentialTable } from 'blb-components/AuthenticatedApp/RON/NotaryPage/CredentialTable';
import styled from 'styled-components';
import { ReviewCredentialModal } from 'blb-components/AuthenticatedApp/RON/NotaryPage/ReviewCredentialModal';
import { useParams } from 'react-router-dom';
import { BlendBridge } from 'blb-types/blendBridge';
import { getUserProfile } from 'blb-api/users';
import { AlertBar, LoadingSpinner } from '@blend/enterprise-components';
import { NotaryStatusButton } from 'blb-components/AuthenticatedApp/RON/NotaryPage/NotaryStatusButton';
import { CredentialTypeNames, NotaryStatus } from 'blb-constants/ron';
import * as Sentry from '@sentry/browser';
import { get } from 'lodash';
import { errorMessage } from 'blb-utils/errors';
import { useToasts } from 'blb-components/AuthenticatedApp/Toast';

const NotaryPageWrapper = styled.div`
  height: 100%;
  background-color: ${c('backgroundGrey')};
`;

export const NotaryPage: React.FC = () => {
  const { principalId } = useParams<{ principalId: string }>();
  const [selectedCredential, setSelectedCredential] = useState<Ron.API.UserCredential>();
  const [credentials, setCredentials] = useState<Ron.API.UserCredential[]>();
  const [userInfo, setUserInfo] = useState<BlendBridge.API.UserProfile>();
  const [loadingError, setLoadingError] = useState<string>();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [notaryStatus, setNotaryStatus] = useState<NotaryStatus>();
  const Toaster = useToasts();

  const fetchUserInfo = async (): Promise<void> => {
    const { data: fetchedUserInfo } = await getUserProfile(principalId);
    setUserInfo(fetchedUserInfo);
  };

  const fetchUserCredentials = async (): Promise<void> => {
    const credentials = await RonAPI.getUserCredentials(principalId);
    setCredentials(credentials);
  };

  const fetchNotaryStatus = async (): Promise<void> => {
    const user = await getRONUser(principalId);
    setNotaryStatus(user.status);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchUserCredentials(), fetchUserInfo(), fetchNotaryStatus()]);
    } catch (error) {
      Sentry.captureException(error);
      setLoadingError(get(error, 'message'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchData();
  }, []);

  const onUpdateNotaryStatus = async (status: NotaryStatus) => {
    try {
      const user = await patchUserStatus(principalId, status);
      setNotaryStatus(user.status);
    } catch (error) {
      Sentry.captureException(error);
      Toaster?.addToast({
        message: get(error, 'response.data.message', errorMessage('UPDATE_NOTARY_STATUS', 'notary')),
        theme: 'error',
      });
    }
  };

  const header = (
    <Box
      align="center"
      alignContent="center"
      background="white"
      direction="row"
      elevation="light"
      flex={{ grow: 0, shrink: 0 }}
      height="xsmall"
      justify="between"
      pad="medium"
    >
      <Heading level={4}>
        {userInfo?.firstName} {userInfo?.lastName}
      </Heading>
      {notaryStatus && <NotaryStatusButton onUpdateStatus={onUpdateNotaryStatus} status={notaryStatus} />}
    </Box>
  );
  const onReview = (credential: Ron.API.UserCredential) => {
    setSelectedCredential(credential);
  };

  const modal = selectedCredential && (
    <FullScreenModal
      bodyPropsOverride={{
        background: 'backgroundGrey',
        margin: { top: 'hair' },
        overflow: 'none',
      }}
      handleClose={() => {
        setSelectedCredential(undefined);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        fetchData();
      }}
      title={CredentialTypeNames[selectedCredential?.type]}
    >
      <ReviewCredentialModal credential={selectedCredential} principalId={principalId} />
    </FullScreenModal>
  );

  const pageBody = (
    <>
      <PageSectionHeader title="Profile" />
      {userInfo && (
        <div>
          <ProfileInfo fetchUserInfo={async () => {}} readonly={true} userInfo={userInfo} />
        </div>
      )}
      <Box margin={{ top: 'large' }}>
        <PageSectionHeader title="Credentials" />
        <Box background="white" width={{ max: 'xlarge' }}>
          {credentials && <CredentialTable credentials={credentials} onReview={onReview} />}
        </Box>
      </Box>
    </>
  );

  const loading = (
    <Box align="center" alignSelf="center" height="100%" justify="center">
      <LoadingSpinner size="lg" theme="secondary" />
    </Box>
  );

  return (
    <NotaryPageWrapper>
      {modal}
      {isLoading ? (
        loading
      ) : (
        <Box background="backgroundGrey">
          {header}
          <Box flex={{ grow: 1, shrink: 1 }} pad="medium">
            {loadingError ? <AlertBar message={loadingError} theme="error" /> : pageBody}
          </Box>
        </Box>
      )}
    </NotaryPageWrapper>
  );
};
